// hooks/calls/useCalls.ts
import { useState, useEffect, useCallback } from "react";
import { Call, BaseCall } from "../../../types/features/Call";
import { callService } from "../../../services/callService";
import { PaginatedResponse } from "../../../types/core/PaginatedResponse";
import { sortByDate } from "../../../helpers/sortDataByDate";

export default function useCalls() {
  const [calls, setCalls] = useState<Call[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<any | null>(null);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    total: 0,
    perPage: 20,
  });

  const fetchStats = useCallback(async () => {
    try {
      const data = await callService.getStats?.();
      if (data) {
        setStats(data);
      }
    } catch (err) {
      console.error("Erreur lors du chargement des statistiques d'appels", err);
    }
  }, []);

  const fetchCalls = useCallback(async (page: number = 1, perPage: number = 20) => {
    setLoading(true);
    setError(null);
    try {
      const response: PaginatedResponse<Call> = await callService.getCalls(page, perPage);
      const dataEnrichies = response.data.map((item) => {
        return {
          ...item,
          client_name: `${item.client?.firstname} ${item.client?.lastname}`,
          client_phone: item.client?.phone,
          client: {
            ...item.client,
          },
        };
      });

      const sortedCalls = sortByDate(dataEnrichies, (call) => call.created_at);

      setCalls(sortedCalls as any);
      setPagination({
        currentPage: response.current_page,
        lastPage: response.last_page,
        total: response.total,
        perPage: response.per_page,
      });
    } catch (err) {
      setError("Erreur lors du chargement des appels");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCallById = useCallback(async (id: string): Promise<Call | null> => {
    setLoading(true);
    setError(null);
    try {
      const call = await callService.getCallById(id);
      if (!call) setError("Appel non trouvé");
      return call;
    } catch (err) {
      setError("Erreur lors du chargement de l'appel");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCallsByClient = useCallback(
    async (clientId: string, page: number = 1, perPage: number = 20): Promise<PaginatedResponse<Call>> => {
      setLoading(true);
      setError(null);
      try {
        const response = await callService.getCallsByClient(clientId, page, perPage);
        return response;
      } catch (err) {
        setError("Erreur lors du chargement des appels du client");
        console.error(err);
        return {
          current_page: 1,
          data: [],
          first_page_url: "",
          from: null,
          last_page: 1,
          last_page_url: "",
          links: [],
          next_page_url: null,
          path: "",
          per_page: perPage,
          prev_page_url: null,
          to: null,
          total: 0,
        };
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const createCall = useCallback(async (data: Omit<Call, "id" | "created_at" | "updated_at">): Promise<Call | null> => {
    setLoading(true);
    setError(null);
    try {
      const newCall = await callService.createCall(data);
      setCalls((prev) => [newCall, ...prev]);
      fetchStats();
      return newCall;
    } catch (err) {
      setError("Erreur lors de la création de l'appel");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const updateCall = useCallback(async (id: string, data: Partial<BaseCall>): Promise<Call | null> => {
    setLoading(true);
    setError(null);
    try {
      const updatedCall = await callService.updateCall(id, data);
      setCalls((prev) => prev.map((c) => (c.id === id ? updatedCall : c)));
      fetchStats();
      return updatedCall;
    } catch (err) {
      setError("Erreur lors de la mise à jour de l'appel");
      console.error(err);
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const deleteCall = useCallback(async (id: string): Promise<boolean> => {
    setLoading(true);
    setError(null);
    try {
      await callService.deleteCall(id);
      setCalls((prev) => prev.filter((c) => c.id !== id));
      fetchStats();
      return true;
    } catch (err) {
      setError("Erreur lors de la suppression de l'appel");
      console.error(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchStats]);

  const getTwilioToken = useCallback(async (): Promise<{ token: string; identity: string } | null> => {
    setError(null);
    try {
      const data = await callService.getTwilioToken();
      return data;
    } catch (err: any) {
      setError("Erreur lors de la récupération du token Twilio");
      console.error(err.response);
      return null;
    }
  }, []);

  useEffect(() => {
    fetchCalls();
    fetchStats();
  }, [fetchCalls, fetchStats]);

  return {
    calls,
    loading,
    error,
    stats,
    pagination,
    fetchCalls,
    fetchStats,
    fetchCallById,
    fetchCallsByClient,
    createCall,
    updateCall,
    deleteCall,
    getTwilioToken,
  };
}